import { Trans } from '@lingui/react/macro';
import { NoddiAsyncError } from 'noddi-types';
import { NoddiButton, NoddiButtonProps, ScreenBaseWrapper, ScreenBaseWrapperWidth } from 'noddi-ui-web';
import { PropsWithChildren, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { NewBookingButton } from '../Common/NewBookingButton';
import { ApiErrorPageWithTrans } from '../Error/ApiErrorPageWithTrans';
import { PageHeading } from './PageHeading';
import { type PageBackground } from './PageLayoutContext';
import { useSetPageBackground } from './usePageBackground';
import { useSetPageWidth } from './usePageWidth';

interface Props {
  title?: string;
  /** ReactNode so a subtitle can emphasize part of itself, e.g. a deadline. */
  subtitle?: ReactNode;
  showNewBookingButton?: boolean;
  showNewBookingButtonVariant?: NoddiButtonProps['variant'];
  customHeaderAction?: ReactNode;
  width?: ScreenBaseWrapperWidth;
  /** Page canvas behind the content; omit for the layout's default. */
  background?: PageBackground;
  className?: string;
  hideBackButton?: boolean;
  error?: NoddiAsyncError | null;
  backButtonPath?: string;
}

export const ContentWrapperCustomerApp = ({
  children,
  title,
  showNewBookingButton,
  showNewBookingButtonVariant = 'primary',
  customHeaderAction,
  subtitle,
  width,
  background,
  className,
  hideBackButton,
  backButtonPath,
  error
}: PropsWithChildren<Props>) => {
  const navigate = useNavigate();
  useSetPageWidth(width);
  useSetPageBackground(background);

  const hasContent = !!title || !!subtitle || showNewBookingButton || !!customHeaderAction;
  return (
    <ScreenBaseWrapper width={width} className={className}>
      {hasContent && (
        <div className='mb-4 flex flex-col gap-2 text-balance'>
          <div>
            {!hideBackButton && (
              <div>
                <NoddiButton
                  startIcon='ArrowLeft'
                  onClick={() => {
                    // oxlint-disable-next-line typescript/no-floating-promises -- both branches are react-router navigate(); the allowlist matches a direct call, not a ternary
                    backButtonPath ? navigate(backButtonPath) : navigate(-1);
                  }}
                  variant='ghost'
                  className='-ml-4'
                >
                  <Trans>Back</Trans>
                </NoddiButton>
              </div>
            )}
            <div className='flex items-center justify-between'>
              {title && (
                <PageHeading className='min-w-0 text-3xl font-bold tracking-tight break-words text-primary md:text-4xl'>
                  {title}
                </PageHeading>
              )}
              <div className='hidden md:block'>
                {customHeaderAction
                  ? customHeaderAction
                  : showNewBookingButton && <NewBookingButton variant={showNewBookingButtonVariant} defaultSize />}
              </div>
            </div>
          </div>
          {subtitle && <p className='text-balance'>{subtitle}</p>}
          <div className='block md:hidden'>
            {customHeaderAction
              ? customHeaderAction
              : showNewBookingButton && <NewBookingButton variant={showNewBookingButtonVariant} />}
          </div>
        </div>
      )}
      {error ? <ApiErrorPageWithTrans error={error} /> : children}
    </ScreenBaseWrapper>
  );
};
